import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Crown, ArrowLeft } from "lucide-react";

const MAX_POLLS = 6;

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [details, setDetails] = useState(null);

  useEffect(() => {
    document.documentElement.classList.add("design-a");
    if (!sessionId) {
      setStatus("missing");
      return;
    }
    let attempts = 0;
    let stop = false;
    const poll = async () => {
      if (stop) return;
      attempts++;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setDetails(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts >= MAX_POLLS) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (e?.response?.status === 404) {
          setStatus("notfound");
          return;
        }
        if (attempts >= MAX_POLLS) {
          setStatus("error");
        } else {
          setTimeout(poll, 2000);
        }
      }
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <div className="design-a min-h-screen flex items-center justify-center px-6 stars-bg">
      <div className="relative bg-[#0a1628] gold-border p-12 max-w-xl w-full text-center">
        {status === "paid" && (
          <>
            <CheckCircle2 size={64} className="mx-auto text-[#ffd700] mb-4" />
            <h1 className="font-display text-5xl font-black gold-text">VICTORY ACHIEVED</h1>
            <p className="mt-4 text-white/80">
              Your patriotic gesture has been recorded in the Eternal Eagle Ledger.
              {details?.metadata?.supporter_email ? " A receipt has been (parodically) dispatched to your inbox." : ""}
            </p>
            {details && (
              <div className="mt-8 border border-[#ffd700]/30 p-5 text-left">
                <Row label="Amount" value={`$${(details.amount_total / 100).toFixed(2)} ${details.currency.toUpperCase()}`} />
                <Row label="Tier" value={
                  details.metadata?.package_id === "mega" ? "MEGA · $400" :
                  details.metadata?.package_id === "american" ? "AMERICAN · $250" :
                  details.metadata?.package_id === "double" ? "DOUBLED · $200" :
                  details.metadata?.package_id === "custom" ? `CUSTOM · $${(details.amount_total / 100).toFixed(2)}` :
                  "STANDARD · $100"
                } />
                <Row label="Patriot" value={details.metadata?.supporter_name || "Anonymous"} />
                <Row label="Session" value={sessionId.slice(0, 20) + "…"} />
              </div>
            )}
            <Crown size={28} className="mx-auto mt-6 text-[#ffd700] blink" />
          </>
        )}

        {status === "checking" && (
          <>
            <div className="w-16 h-16 mx-auto border-4 border-[#ffd700]/30 border-t-[#ffd700] rounded-full animate-spin" />
            <h1 className="mt-6 font-display text-4xl font-black gold-text">DISPATCHING EAGLES…</h1>
            <p className="mt-3 text-white/70">Confirming your payment with Stripe.</p>
          </>
        )}

        {(status === "expired" || status === "timeout" || status === "error") && (
          <>
            <h1 className="font-display text-4xl font-black text-[#b22234]">
              {status === "expired" ? "Session Expired" : "Status Unavailable"}
            </h1>
            <p className="mt-3 text-white/70">
              If you were charged, your transaction is safe — but we couldn't
              confirm the status in time. Check your email for the Stripe receipt.
            </p>
          </>
        )}

        {status === "notfound" && (
          <>
            <h1 data-testid="success-notfound" className="font-display text-4xl font-black text-[#b22234]">Session Not Found</h1>
            <p className="mt-3 text-white/70">
              That checkout session id does not exist. Maybe try again, patriot.
            </p>
          </>
        )}

        {status === "missing" && (
          <h1 className="font-display text-3xl text-white">No session id provided.</h1>
        )}

        <Link
          data-testid="back-home"
          to="/"
          className="mt-10 inline-flex items-center gap-2 font-accent text-[12px] tracking-[0.3em] text-[#ffd700] hover:text-white"
        >
          <ArrowLeft size={14} /> Back to the Drive
        </Link>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/10 last:border-b-0 text-sm">
      <span className="text-white/60 uppercase tracking-wider text-[10px]">{label}</span>
      <span className="text-white font-display">{value}</span>
    </div>
  );
}
